import { useParams, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Navbar } from "../components/landing/Navbar";
import { Hero } from "../components/landing/Hero";
import { Marquee } from "../components/landing/Marquee";
import { CalculatorPreview } from "../components/landing/CalculatorPreview";
import { HowItWorks } from "../components/landing/HowItWorks";
import { ProjectInfo } from "../components/landing/ProjectInfo";
import { VisualBreak } from "../components/landing/VisualBreak";
import { RelatedCalculators } from "../components/landing/RelatedCalculators";
import { FAQ } from "../components/landing/FAQ";
import { FinalCTA } from "../components/landing/FinalCTA";
import { Footer } from "../components/landing/Footer";
import { PAGES } from "../config/pages";
import { SITE } from "../config/site";
import { usePagesData } from "../hooks/usePagesData";

const NotFound = () => (
  <div className="flex min-h-screen flex-col items-start justify-center bg-ink-bg px-6" data-testid="not-found-page">
    <p className="font-mono text-xs uppercase tracking-[0.35em] text-accent">404 // Off the plans</p>
    <h1 className="mt-6 font-display text-5xl uppercase leading-none tracking-tight text-ink-text sm:text-6xl">
      This page isn't on the blueprint.
    </h1>
    <Link
      to="/"
      data-testid="not-found-home-link"
      className="mt-10 inline-flex items-center gap-2 bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
    >
      Back to all calculators
    </Link>
  </div>
);

export default function LandingPage() {
  const { slug } = useParams();
  const apiPages = usePagesData();
  const source = apiPages ? { ...PAGES, ...apiPages } : PAGES;
  const page = source[slug];

  if (!page) {
    return (
      <>
        <SEO title="Page Not Found | MatCalc360" description="The page you are looking for does not exist." slug={slug} />
        <Navbar />
        <NotFound />
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={page.seoTitle}
        description={page.metaDescription}
        slug={page.slug}
        image={page.heroImage}
        faqs={page.faqs}
      />
      <Navbar />
      <main data-testid={`landing-page-${page.slug}`}>
        <Hero
          kicker={page.kicker}
          lines={page.h1Lines}
          intro={page.intro}
          primaryCta={page.primaryCta}
          secondaryCta={page.secondaryCta}
          image={page.heroImage}
          imageAlt={page.heroImageAlt}
          testidPrefix="hero"
        />
        <Marquee />
        <CalculatorPreview calculator={page.calculator} slug={page.slug} />
        <HowItWorks />
        <ProjectInfo title={page.guideTitle} chapters={page.chapters} internalSlugs={page.relatedSlugs} pagesSource={source} />
        <VisualBreak image={page.visualImage} imageAlt={page.visualImageAlt} stat={page.visualStat} />
        <RelatedCalculators items={page.relatedSlugs.map((s) => source[s]).filter(Boolean)} />
        <FAQ faqs={page.faqs} />
        <FinalCTA
          ctaLabel={page.ctaText}
          ctaHref={`${SITE.appBaseUrl}${page.calculator.path}`}
          note="Free. No signup. Built for the jobsite."
        />
      </main>
      <Footer />
    </>
  );
}
